import { useState } from 'react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import {
  Button,
  Card,
  CardTitle,
  EmptyState,
  FilterSelect,
  HeroActions,
  HeroBar,
  HeroBody,
  HeroMeta,
  HeroTitle,
  PageBody,
  Spinner,
} from '@wundo/ui';
import { ApiError } from '@wundo/client/api.ts';
import { PageHeader } from '@/components/PageHeader.tsx';
import { SkillDefinitionCard } from '@/components/skills/SkillDefinitionCard.tsx';
import { SkillRecentOutreaches } from '@/components/skills/SkillRecentOutreaches.tsx';
import { SkillRecentUsers } from '@/components/skills/SkillRecentUsers.tsx';
import { SkillStatTiles } from '@/components/skills/SkillStatTiles.tsx';
import { fetchSkill, fetchSkillStats } from '@/lib/skills.ts';

export const Route = createFileRoute('/_app/skills/$id/')({
  staticData: { orgScope: 'none' },
  component: SkillDetailRoute,
});

function SkillDetailRoute() {
  return <SkillDetailPage id={Route.useParams().id} />;
}

const WINDOWS = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
] as const;

type WindowValue = (typeof WINDOWS)[number]['value'];

/* A client error says what went wrong; a server error stays generic. */
function statsErrorMessage(error: Error | null): string {
  if (error instanceof ApiError && error.status !== null && error.status < 500) {
    return error.detail ?? 'Failed to load skill stats';
  }
  return error ? 'Failed to load skill stats' : '';
}

/**
 * One skill: what it says, and what it has done.
 *
 * The definition is fixed; everything under "Performance" is read against a
 * trailing window, which is the one control on the page.
 */
export function SkillDetailPage({ id }: { id: string }) {
  const router = useRouter();
  const [windowDays, setWindowDays] = useState<WindowValue>('30');

  const skill = useQuery({
    queryKey: ['skills', 'detail', id],
    queryFn: () => fetchSkill(id),
    retry: false,
  });
  const stats = useQuery({
    queryKey: ['skills', 'stats', id, windowDays],
    queryFn: () => fetchSkillStats(id, Number(windowDays)),
    retry: false,
  });

  if (skill.isPending) {
    return (
      <PageBody className='flex justify-center'>
        <Spinner />
      </PageBody>
    );
  }

  if (skill.error) {
    /* Not found is a state the page can draw; anything else is the shell's. */
    if (!(skill.error instanceof ApiError) || skill.error.status !== 404) {
      throw skill.error;
    }

    return (
      <>
        <PageHeader title='Skill' />
        <PageBody>
          <Card>
            <EmptyState title='Skill not found' description='It may have been deleted.' />
          </Card>
        </PageBody>
      </>
    );
  }

  const statsError = statsErrorMessage(stats.error);

  /* The hero carries the h1; PageTitle would make it the page's second. */
  return (
    <PageBody flush className='flex flex-col gap-5'>
      <HeroBar>
        <HeroBody>
          <HeroTitle>{skill.data.name}</HeroTitle>
          <HeroMeta>{skill.data.description}</HeroMeta>
        </HeroBody>
        <HeroActions>
          <Button
            variant='onPrimary'
            size='sm'
            onClick={() =>
              void router.navigate({ to: '/skills/$id/edit', params: { id } })
            }
          >
            <Pencil className='size-4' aria-hidden='true' />
            Edit
          </Button>
        </HeroActions>
      </HeroBar>

      <SkillDefinitionCard skill={skill.data} />

      <div className='flex items-center justify-between gap-4'>
        <h2 className='text-base font-semibold text-ink'>Performance</h2>
        <FilterSelect
          hideLabel
          label='Window'
          options={WINDOWS}
          value={windowDays}
          onValueChange={setWindowDays}
        />
      </div>

      {statsError !== '' && (
        <p
          role='alert'
          className='rounded-md bg-danger-fill px-3.5 py-2.5 text-sm font-semibold text-danger'
        >
          {statsError}
        </p>
      )}

      {stats.data && (
        <>
          <SkillStatTiles stats={stats.data} />
          <div className='grid grid-cols-1 gap-3.5 agenda:grid-cols-2'>
            <SkillRecentUsers recent={stats.data.usage.recent} />
            <SkillRecentOutreaches recent={stats.data.recommendations.recent} />
          </div>
        </>
      )}

      <Card as='section' aria-label='System prompt'>
        <CardTitle as='h3'>System prompt</CardTitle>
        <pre className='mt-3.5 max-h-64 overflow-y-auto rounded-md bg-fill-faint p-3.5 font-mono text-xs break-words whitespace-pre-wrap text-ink-secondary'>
          {skill.data.prompt}
        </pre>
      </Card>
    </PageBody>
  );
}
